import { OnlineStatusContext } from '@/contexts/onlineStatus';
import { useCurrentUser, useEnabledFeatures } from '@/hooks/dataHelpers';
import { useRealtimeDbBatchMutation, useRealtimeDbProviderName } from '@/hooks/realtimeDataHelpers';
import { saveItemToOfflineDB } from '@/lib/offlineDatabase/OfflineDatabase';
import { isRetakeOpen } from '@/pages/experiences/show/take/retakeVisibility';
import {
  selectAttemptNoFromUserInfo,
  selectSetLocalUserInfo,
  selectUserInfo,
  useRealtimeExperienceStore,
} from '@/pages/experiences/store/realtimeExperienceStore';
import {
  selectExperience,
  selectExperienceViewMode,
  useExperienceStore,
} from '@/store/experienceStore';
import * as Sentry from '@sentry/react';
import { useContext, useEffect, useRef } from 'react';
import { buildRealtimeLogPayload } from '../../utils/realtimeExperienceHelper';

export interface RealtimeUserInfoHookReturnProps {
  loading: boolean;
  user_info: any;
  setUserInfo: (userInfo: any, options?: any) => Promise<void>;
}

export const useRealtimeUserInfo = (): RealtimeUserInfoHookReturnProps => {
  const currentUser = useCurrentUser();
  const enabledFeatures = useEnabledFeatures();
  const experience = useExperienceStore(selectExperience);
  const userInfoFromStore = useRealtimeExperienceStore(selectUserInfo);
  const attemptNoFromUserInfo = useRealtimeExperienceStore(selectAttemptNoFromUserInfo);
  const setLocalUserInfo = useRealtimeExperienceStore(selectSetLocalUserInfo);
  const experienceViewMode = useExperienceStore(selectExperienceViewMode);
  const { isOnline, userIp } = useContext(OnlineStatusContext);

  const currentUserId = currentUser?.id;
  const currentUserRole = currentUser?.role;
  const isStudent = currentUserRole === 'student';
  const isTeacherOrAdmin = ['teacher', 'admin'].includes(currentUserRole);
  const dataProviderName = useRealtimeDbProviderName();
  const isSupabaseProvider = dataProviderName === 'supabase';
  const { batchMutation, loading } = useRealtimeDbBatchMutation();
  // apply useCallback on setUserInfo

  console.log(
    'setUserInfo realtimeUSerInfo => userInfoFromStore',
    userInfoFromStore.currentView,
    experienceViewMode,
    isTeacherOrAdmin,
    experience?.experience_user
  );

  const setUserInfo = async (userInfoFromProps: any, options?: any) => {
    try {
      console.log('setUserInfo realtimeUSerInfo => try condition');
      const userInfo = {
        ...userInfoFromProps,
      };
      if (isStudent) {
        userInfo.student_updated_at = Date.now();
      } else {
        userInfo.teacher_updated_at = Date.now();
      }
      if (
        // An open retake writes despite the graded EU — else
        // the monitor is blind to the new attempt (StartTestAction idiom).
        (isStudent &&
          experienceViewMode === 'apTakeTest' &&
          (!experience?.experience_user ||
            isRetakeOpen(experience, enabledFeatures, attemptNoFromUserInfo))) ||
        // Teachers/Admins: write in non-preview
        (isTeacherOrAdmin && experienceViewMode !== 'apPreviewTest')
      ) {
        console.log('setUserInfo realtimeUSerInfo => if condition', userInfo);
        // We don't need to save to local for UserInfo, as RealtimeDBProvider handling live data well

        const updateValues: any[] = [];
        // Coerce once: Dexie's where() is type-strict — a string user_id would
        // miss the numeric row in the offline DB and fork a duplicate.
        const targetUserId = Number(isTeacherOrAdmin ? userInfo.user_id : currentUserId);
        const uuidKey = `eId${experience.id}_uId${targetUserId}`;
        const tracksPendingSync = isStudent && isSupabaseProvider;
        // Values from earlier failed saves ride this payload; payload keys win.
        const outgoingUserInfo = tracksPendingSync
          ? { ...useRealtimeExperienceStore.getState().pending_user_info, ...userInfo }
          : userInfo;
        const userInfoData = {
          // resource we need for firebase to upsert by resource
          resource: 'user_info',
          resourcePath: `experiences/${experience.id}/`,
          // uuid we need for supabase to upsert by uuid
          uuids: [uuidKey],
          // ids we need for firebase to upsert by id
          ids: [targetUserId],
          // data we need to upsert
          data: outgoingUserInfo,
        };
        if (tracksPendingSync) {
          // Reflect the student's own progress locally, up front and independent of the
          // Supabase write result. In China (Supabase) the write is a plain REST call
          // with no offline cache, so a failed/offline save would leave progress frozen:
          // createMany now throws on failure (for the honest 'failed' status + Sentry),
          // which skips the onSuccess that used to set this — so we set it here instead.
          // Scoped to Supabase only: the global/Firestore path is driven by the live
          // snapshot (fed from Firestore's offline cache) and must not get optimistic
          // writes for unrelated user_info fields. setLocalUserInfo merges, so partial
          // updates are safe. The cloud icon separately shows saved/failed. (APL-11925)
          setLocalUserInfo(userInfo);
          try {
            const offlineUserInfo = { ...userInfo };
            if (userInfo?.accessibility) {
              // The Supabase upsert and Dexie .update() both REPLACE the map, so send the MERGED map — a partial would amputate untouched fields.
              outgoingUserInfo.accessibility =
                useRealtimeExperienceStore.getState().user_info?.accessibility;
              offlineUserInfo.accessibility = outgoingUserInfo.accessibility;
            }
            delete offlineUserInfo.id;
            saveItemToOfflineDB(
              'userInfo',
              { experience_id: experience.id, user_id: targetUserId },
              offlineUserInfo
            );
          } catch (error) {
            console.error('Failed to save user info to offline database:', error);
            Sentry.captureException(error);
          }
        }

        updateValues.push(userInfoData);

        // Accept a single `log` or a batched `logs` array, and append every entry to
        // the SAME mutation so one save's audit logs all land in one write (no race).
        const { log, logs, studentIsOnline } = options ?? {};
        const allLogs = (Array.isArray(logs) ? logs : log ? [log] : []).filter(
          (entry: any) => entry?.logging
        );

        for (const logEntry of allLogs) {
          const realtimeLogPayload = buildRealtimeLogPayload(logEntry, {
            currentUser,
            experience,
            userIp,
            // Teacher writes pass the student's status; student writes fall back to self.
            isOnline: studentIsOnline ?? isOnline,
            collection: 'experiences',
            multipleAttemptsEnabled: !!enabledFeatures?.multiple_attempts,
            // Realtime doc first: during an open retake it is the ONLY place the
            // new attempt number exists.
            attemptNo: attemptNoFromUserInfo ?? experience?.experience_user?.attempt_no,
          });
          if (currentUser.role === 'student') {
            try {
              saveItemToOfflineDB(
                'logs',
                { log_id: realtimeLogPayload?.data?.log_id },
                realtimeLogPayload.data
              );
            } catch (error) {
              console.error('Failed to save log to offline database:', error);
              Sentry.captureException(
                `Failed to save log to offline database in useRealtimeUserInfo: ${error}`
              );
            }
          }
          updateValues.push(realtimeLogPayload);
        }

        // console.log('setUserInfo updateValues ==>', JSON.stringify(updateValues));
        // if (currentUser.role === 'student') {
        //   // Saving to offline before online
        //   try {
        //     saveItemToOfflineDB(
        //       'userInfo',
        //       { experience_id: experience.id, user_id: currentUserId },
        //       userInfoData
        //     );
        //   } catch (error) {
        //     console.error('Failed to save user info to offline database:', error);
        //   }
        // }

        batchMutation({
          values: updateValues,
          meta: {
            onSuccess: (batchResult: any) => {
              if (tracksPendingSync) {
                // Everything pending rode this write — nothing partial to keep.
                useRealtimeExperienceStore.getState().setPendingUserInfo({}, { merge: false });
              }
              if (options?.onSuccess) {
                options.onSuccess(batchResult);
              }
              // Local student progress is now set optimistically up front (see the
              // tracksPendingSync block above), so it survives a
              // failed/offline save — no need to re-set it here on the success path
              // (APL-11925).
            },
            onError: (error: any) => {
              if (tracksPendingSync) {
                useRealtimeExperienceStore.getState().setPendingUserInfo(userInfo);
              }
              Sentry.captureException(new Error(error?.message ?? 'setUserInfo failed'), {
                extra: {
                  op: 'realtimeUserInfo:setUserInfo',
                  status: error?.statusCode ?? error?.response?.status,
                },
              });
              if (options?.onError) {
                options.onError(error);
              }
            },
          },
        });
      } else {
        console.log('setLocalUserInfoElseParams', options);
        console.log('setUserInfo realtimeUSerInfo => else condition ', options);

        setLocalUserInfo(userInfo);
        if (options?.onSuccess) {
          options.onSuccess(userInfo);
        }
      }
    } catch (error) {
      console.log('setUserInfo realtimeUSerInfo => catch condition ', error);
      console.log(error, 'error in useRealtimeUserInfo', error);
      Sentry.captureException(new Error((error as any)?.message ?? 'setUserInfo threw'), {
        extra: {
          op: 'realtimeUserInfo:setUserInfo:catch',
          status: (error as any)?.statusCode ?? (error as any)?.response?.status,
        },
      });
      if (options?.onError) {
        options.onError(error);
      }
    }
  };

  // Reconnect push: send pendings at once instead of waiting for the ~30s backup tick.
  const prevIsOnlineRef = useRef(isOnline);
  useEffect(() => {
    const cameOnline = !prevIsOnlineRef.current && isOnline;
    prevIsOnlineRef.current = isOnline;
    if (!cameOnline || !isStudent || !isSupabaseProvider || !experience?.id) return;
    const pending = useRealtimeExperienceStore.getState().pending_user_info ?? {};
    if (Object.keys(pending).length === 0) return;
    setUserInfo({});
    // Flip-edge detection only — the other inputs are read fresh per flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  return {
    loading,
    user_info: userInfoFromStore,
    setUserInfo,
  };
};
